import { analyticsGridKey } from "../../../../../utils/helpers/analyticsGridKey";
import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Header, LayerContent } from "../../fall-analysis/style";
import { TableContainer } from "../../../../../components/DashboardComps/Dashboard.styles";
import DynamicGrid from "../../../../../components/DynamicGrid/DynamicGrid";
import { useAnalyticsWrapper } from "../../../../../store/store";
import useAnalytics from "../../../../../hooks/useAnalytics";
import { caregiverAuditBreakdownCols } from "../../dummyData/colums";

// Caregiver Audit breakdown (MARCC-773 / MARCC-788): one row per matching incident,
// showing all four caregiver roles side by side. Reads the SAME overview query as
// CaregiverAuditSection (identical facility/date/incidentTypes/caregiverIds params),
// so React Query serves it from cache — no extra network call.
//
// Uses DynamicGrid, same as the Incident List report, which brings the "Include
// Columns" toggles (for the `optional` columns), search, sort, filter and export.
// `wrapHeaders` is required here because this grid's long role headers are always
// visible — Kendo truncates headers by default. Matching the caregiver(s) currently
// selected highlights their name via the "matchedCaregiver" cell type.
//
// `shareExportData` lifts the rows + currently-visible columns to the report's
// top-level Excel export, so it reflects the user's Include Columns toggles.
const CaregiverAuditBreakdown = ({
  shareExportData,
  isPdfExporting = false,
}: {
  shareExportData?: (payload: {
    columns: { header: string; accessor: string }[];
    rows: any[];
  }) => void;
  isPdfExporting?: boolean;
}) => {
  const {
    startDate,
    endDate,
    caregiverAuditFacility,
    caregiverAuditIncidentTypes,
    caregiverAuditCaregiverIds,
  } = useAnalyticsWrapper((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    caregiverAuditFacility: state.caregiverAuditFacility,
    caregiverAuditIncidentTypes: state.caregiverAuditIncidentTypes,
    caregiverAuditCaregiverIds: state.caregiverAuditCaregiverIds,
  }));

  const { getCaregiverAuditOverview } = useAnalytics({
    facility: caregiverAuditFacility?.value || "",
    startDate: moment(startDate).format("yyyy-MM-DD"),
    endDate: moment(endDate).format("yyyy-MM-DD"),
    incidentTypes: (caregiverAuditIncidentTypes || []).join(","),
    caregiverIds: (caregiverAuditCaregiverIds || []).join(","),
    queries: ["caregiverAuditOverview"],
  });

  const { data, isFetching } = getCaregiverAuditOverview;

  const selectedIds = useMemo(
    () => new Set<string>(caregiverAuditCaregiverIds || []),
    [caregiverAuditCaregiverIds]
  );

  const rows = useMemo(() => {
    const breakdown = data?.data?.data?.breakdown;
    return Array.isArray(breakdown) ? breakdown : [];
  }, [data]);

  const gridCols = useMemo(
    () => caregiverAuditBreakdownCols(selectedIds),
    [selectedIds]
  );

  // Report-level PDFExport rasterizes this grid directly, bypassing DynamicGrid's own
  // PDF export, so "view" needs to be dropped here too or it prints as a dead icon.
  const pdfSafeGridCols = useMemo(
    () => gridCols.filter((col: any) => col.customCellType !== "view"),
    [gridCols]
  );

  // Which `optional` columns the user currently has ticked on, reported by
  // DynamicGrid. Compared by value before setting state — DynamicGrid fires this
  // from an effect, so a new array identity every render would loop.
  const [visibleOptionalFields, setVisibleOptionalFields] = useState<string[]>(
    []
  );
  const handleOptionalColumnsChange = useCallback((fields: string[]) => {
    setVisibleOptionalFields((prev) =>
      prev.length === fields.length && prev.every((f, i) => f === fields[i])
        ? prev
        : fields
    );
  }, []);

  const exportColumns = useMemo(
    () =>
      gridCols
        // "view" is an icon column — meaningless in a spreadsheet
        .filter((col: any) => col.customCellType !== "view")
        .filter(
          (col: any) =>
            !col.optional || visibleOptionalFields.includes(col.accessor)
        )
        .map((col: any) => ({ header: col.Header, accessor: col.accessor })),
    [gridCols, visibleOptionalFields]
  );

  useEffect(() => {
    shareExportData?.({ columns: exportColumns, rows });
  }, [exportColumns, rows, shareExportData]);

  return (
    <LayerContent>
      <Header>Caregiver Breakdown</Header>
      <TableContainer>
        <DynamicGrid
          gridCols={isPdfExporting ? pdfSafeGridCols : gridCols}
          gridData={rows}
          isLoading={isFetching}
          wrapHeaders={true}
          renderAllRows={isPdfExporting}
          onOptionalColumnsChange={handleOptionalColumnsChange}
          showTitleBar={false}
          startDate={startDate}
          endDate={endDate}
          facilityName={caregiverAuditFacility?.label}
          reportName="Caregiver Breakdown"
          viewStateKey={analyticsGridKey(
            "caregiver-breakdown",
            startDate,
            endDate,
            caregiverAuditFacility?.label,
            JSON.stringify(caregiverAuditIncidentTypes),
            JSON.stringify(caregiverAuditCaregiverIds)
          )}
          totalRowHighlight={false}
        />
      </TableContainer>
    </LayerContent>
  );
};

export default CaregiverAuditBreakdown;
